import React, { useContext, useState } from "react";
import { FileDown, Lock, Loader2, Printer, FileText } from "lucide-react";
import { NotesCtx } from "./LessonNotes";

const esc = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const today = () => new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

// Decrypts on this device only; nothing is sent anywhere.
const collect = async (notes, section, blanks) => {
  const lessons = section.items.filter((it) => it.type !== "artwork");
  const rows = await Promise.all(lessons.map(async (it, i) => ({ n: i + 1, title: it.title || `Lesson ${i + 1}`, text: notes.has(it.id) ? (await notes.read(it.id)).trim() : "" })));
  return rows.filter((r) => blanks || r.text);
};

const asText = (course, from, rows) => [`${course}`, `Study sheet${from ? ` from ${from}` : ""}, ${today()}`, "",
  ...rows.flatMap((r) => [`${r.n}. ${r.title}`, r.text || "(No notes yet)", ""])].join("\n");

const asHtml = (course, from, rows) => `<!doctype html><html><head><meta charset="utf-8"><title>${esc(course)} study sheet</title><style>
body{font-family:Georgia,serif;color:#1a1a1a;max-width:720px;margin:40px auto;padding:0 24px;line-height:1.55}
h1{font-size:26px;margin:0 0 4px}.sub{color:#666;font-size:13px;margin-bottom:28px}
h2{font-size:16px;margin:24px 0 6px;padding-bottom:4px;border-bottom:1px solid #ddd}
p{white-space:pre-wrap;margin:0;font-size:14px}.blank{color:#999;font-style:italic;min-height:60px}
@media print{body{margin:0}h2{break-after:avoid}}</style></head><body>
<h1>${esc(course)}</h1><div class="sub">Study sheet${from ? ` from ${esc(from)}` : ""}, ${today()}</div>
${rows.map((r) => `<h2>${r.n}. ${esc(r.title)}</h2>${r.text ? `<p>${esc(r.text)}</p>` : `<p class="blank">No notes yet</p>`}`).join("")}
</body></html>`;

const saveText = (name, text) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  a.download = `${name.replace(/[^\w\s]/g, "").trim().replace(/\s+/g, "-") || "notes"}-study-sheet.txt`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

export const NotesExport = ({ section, accent }) => {
  const notes = useContext(NotesCtx);
  const [open, setOpen] = useState(false);
  const [blanks, setBlanks] = useState(notes?.blanks !== false);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  if (!notes || !section.items.some((it) => it.type !== "artwork")) return null;
  const count = section.items.filter((it) => notes.has(it.id)).length;
  const run = async (kind) => {
    setMsg("");
    const win = kind === "pdf" ? window.open("", "_blank") : null;
    setBusy(kind);
    try {
      const rows = await collect(notes, section, blanks);
      if (!rows.length) { win?.close(); setMsg("No notes in this course yet."); return; }
      if (kind === "txt") { saveText(section.title, asText(section.title, notes.title, rows)); return; }
      if (!win) { setMsg("Please allow pop ups to save as PDF."); return; }
      win.document.write(asHtml(section.title, notes.title, rows)); win.document.close();
      setTimeout(() => win.print(), 300);
    } finally { setBusy(""); }
  };
  return (
    <div className="mb-3" data-testid={`notes-export-${section.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`notes-export-toggle-${section.id}`} className="inline-flex items-center gap-1.5 font-body text-xs text-zinc-400 hover:text-white">
        <FileDown size={13} /> Download my notes{count > 0 && ` (${count})`}
      </button>
      {open && (notes.locked
        ? <p className="font-body text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5"><Lock size={11} /> Unlock your vault to download your notes.</p>
        : <div className="mt-2 border border-white/10 bg-[#0A0A0A] p-3 space-y-2.5" data-testid={`notes-export-panel-${section.id}`}>
            <label className="flex items-center gap-2 font-body text-xs text-zinc-300">
              <input type="checkbox" checked={blanks} onChange={(e) => setBlanks(e.target.checked)} data-testid={`notes-export-blanks-${section.id}`} style={{ accentColor: accent }} /> Include lessons without notes
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={!!busy || !notes.ready} onClick={() => run("pdf")} data-testid={`notes-export-pdf-${section.id}`} className="inline-flex items-center gap-1.5 border px-3 py-1.5 font-body text-xs text-white disabled:opacity-40" style={{ borderColor: accent }}>
                {busy === "pdf" ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />} Printable PDF
              </button>
              <button type="button" disabled={!!busy || !notes.ready} onClick={() => run("txt")} data-testid={`notes-export-txt-${section.id}`} className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-body text-xs text-white disabled:opacity-40">
                {busy === "txt" ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Text file
              </button>
            </div>
            {msg && <p className="font-body text-xs text-amber-400/90" data-testid={`notes-export-msg-${section.id}`}>{msg}</p>}
            <p className="font-body text-[11px] text-zinc-500 flex items-start gap-1.5"><Lock size={10} className="mt-0.5 shrink-0" /> Made on your device. Your notes are unlocked here and never sent anywhere. The saved file is readable by anyone with it, so keep it somewhere private.</p>
          </div>)}
    </div>
  );
};
